import React, { forwardRef } from 'react'
import styled from 'styled-components'

const animationEasing = 'cubic-bezier(0.175, 0.885, 0.32, 1.175)'

const SwitchLabel = styled.label<{ $height: number; $disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  position: relative;
  width: ${({ $height }) => $height * 2}px;
  height: ${({ $height }) => $height}px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
`

const SwitchTrack = styled.div<{ $height: number }>`
  width: ${({ $height }) => $height * 2}px;
  height: ${({ $height }) => $height}px;
  border-radius: 9999px;
  background-color: #dfe3e8;
  position: relative;
  transition: background-color 120ms ease-in-out, box-shadow 120ms ease-in-out;
  display: flex;
  align-items: center;
`

const IconContainer = styled.div<{ $height: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ $height }) => $height}px;
  padding-left: 4px;
  opacity: 0;
  transform: scale(0.8);
  transition: all 500ms ${animationEasing};

  svg {
    transition: all 500ms ${animationEasing};
    transform: scale(0);
  }
`

const HandleContainer = styled.div<{ $height: number }>`
  width: ${({ $height }) => $height * 2}px;
  height: 100%;
  display: flex;
  transform: translateX(0%);
  transition: transform 200ms ease-in-out;
`

const Handle = styled.div<{ $height: number }>`
  width: ${({ $height }) => $height}px;
  height: ${({ $height }) => $height}px;
  padding: 2px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: '';
    width: ${({ $height }) => $height - 4}px;
    height: ${({ $height }) => $height - 4}px;
    border-radius: 9999px;
    background: #fff;
    box-shadow: 0 2px 4px rgba(67, 90, 111, 0.3);
    display: block;
  }
`

const HiddenInput = styled.input`
  border: 0;
  clip: rect(1px, 1px, 1px, 1px);
  height: 1px;
  width: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  opacity: 0;

  & + ${SwitchTrack} {
    transition: background-color 120ms ease-in-out, box-shadow 120ms ease-in-out;
  }

  &:focus-visible + ${SwitchTrack} {
    box-shadow: 0 0 0 2px rgba(16, 112, 202, 0.4);
  }

  &:checked + ${SwitchTrack} {
    background-color: ${({ theme }) => theme.colors.primary};
  }

  &:disabled + ${SwitchTrack} {
    cursor: not-allowed;
  }

  &:checked + ${SwitchTrack} ${HandleContainer} {
    transform: translateX(50%);
  }

  &:checked + ${SwitchTrack} ${IconContainer} {
    opacity: 1;
    transform: scale(1);
  }

  &:checked + ${SwitchTrack} ${IconContainer} svg {
    transform: scale(1);
  }
`

const CheckIcon: React.FC<{ size: number; fill?: string }> = ({ size, fill = 'currentColor' }) => (
  <svg width={10} height={size} viewBox="0 0 10 7" fill="none">
    <path
      fill={fill}
      fillRule="evenodd"
      d="M4 4.586L1.707 2.293A1 1 0 1 0 .293 3.707l3 3a.997.997 0 0 0 1.414 0l5-5A1 1 0 1 0 8.293.293L4 4.586z"
    />
  </svg>
)

export interface SwitchProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  height?: number
  hasCheckIcon?: boolean
}

export const Switch = forwardRef<HTMLInputElement, SwitchProps>(function Switch(
  { height = 16, disabled, hasCheckIcon = false, ...props },
  ref
) {
  return (
    <SwitchLabel $height={height} $disabled={disabled}>
      <HiddenInput ref={ref} type="checkbox" disabled={disabled} {...props} />
      <SwitchTrack $height={height}>
        {hasCheckIcon && (
          <IconContainer $height={height}>
            <CheckIcon size={height / 2 - 3} fill="#fff" />
          </IconContainer>
        )}
        <HandleContainer $height={height}>
          <Handle $height={height} />
        </HandleContainer>
      </SwitchTrack>
    </SwitchLabel>
  )
})

Switch.displayName = 'Switch'
